import { DailyRecurrenceDetails, HourlyRecurrenceDetails, MonthlyRecurrenceDetails, Recurrence, RecurrenceConverters, RecurrenceMode, RecurrenceType, ScheduleDescriptions,StaticInitializers } from '@tableau/self-service-schedules';
import { LocalizationHelpers } from '@tableau/self-service-schedules/lib/src/components/CreateOrEditRecurrenceModelMapper';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { isFlowSelfServiceSchedulesEnabled } from '~src/flow/utils/FlowSelfServiceSchedulesUtils';

import { SessionServerSelectors,SessionSiteSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '../../bootstrap/redux/store/AppStore';
import { describeLegacyAdminSchedule } from '../utils/ScheduleFrequencyDescriptionFilter';
import { DefinedBy, ISchedule, ScheduleActions } from './ISchedule';

export interface IScheduleInfo extends ISchedule {
  contentType: 'schedule';
}

export namespace IScheduleInfo {

  // This is supposedly not the "optimal" cloning method
  // https://stackoverflow.com/questions/122102/what-is-the-most-efficient-way-to-deep-clone-an-object-in-javascript
  // But even though there are tests to ensure it works as expected, the Object.assign approach doesn't seem to work
  function clone<T>(x: T) {
    return JSON.parse(JSON.stringify(x)) as T;
  }

  export function toSSSRecurrence(schedule: ISchedule): Recurrence {
    // This function modifies key attributes and as such needs to make a copy of the data to avoid any unexpected side-effects
    const scheduleClone: ISchedule = clone(schedule);
    let recurrence = {
      recurrenceDetails: scheduleClone.scheduleDetails as any,
      recurrenceMode: RecurrenceMode.TimeRecurrence,
      recurrenceType: scheduleClone.scheduleType as RecurrenceType,
    };
    type invalid = any;
    switch (recurrence.recurrenceType) {
      case RecurrenceType.Hourly:
        // SSS expects both Hourly and Daily schedules to have recurrenceDaysOfWeek to be set. In the Admin Schedules these schedule
        // types did not have those values set, and that was interpretted to mean "every day of the week"
        if (!('recurrenceDaysOfWeek' in recurrence.recurrenceDetails)) {
          (recurrence.recurrenceDetails as invalid).recurrenceDaysOfWeek = StaticInitializers.DEFAULT_ALL_DAYS_A_WEEK();
        }
        break;
      case RecurrenceType.Daily:
        // SSS expects both Hourly and Daily schedules to have recurrenceDaysOfWeek to be set. In the Admin Schedules these schedule
        // types did not have those values set, and that was interpretted to mean "every day of the week"
        if (!('recurrenceDaysOfWeek' in recurrence.recurrenceDetails)) {
          (recurrence.recurrenceDetails as invalid).recurrenceDaysOfWeek = StaticInitializers.DEFAULT_ALL_DAYS_A_WEEK();
        }
        // SSS expects end time to equal start time if the schedule runs "all day"
        if (!('endTime' in recurrence.recurrenceDetails)) { // ends when it starts...
          (recurrence.recurrenceDetails as invalid).endTime = (recurrence.recurrenceDetails as invalid).startTime;
        }
        // SSS expects recurrence minutes to be the number of minutes in a day if the schedule runs on a 24 hour recurrence (daily)
        if (!('recurrenceMinutes' in recurrence.recurrenceDetails)) {
          (recurrence.recurrenceDetails as invalid).recurrenceMinutes = StaticInitializers.MINUTES_IN_DAY;
        }
        // SSS does not allow recurrence minutes to be less than 2 hours for daily. If it is it's a legacy schedule
        if (recurrence.recurrenceDetails.recurrenceMinutes < 60 * 2) {
          (recurrence.recurrenceDetails as invalid).recurrenceMinutes = StaticInitializers.MINUTES_IN_DAY;
        }
        break;
    }
    return recurrence;
  }

  // Duplicate from SSS because apparently the expected behavior in SSS/VPC is not the same
  export function toDaysOfMonthArr(recurrenceDayOfMonth: string): number[] {
    let daysOfMonthMask = parseInt(recurrenceDayOfMonth, 10);
    if (isNaN(daysOfMonthMask)) {
      throw recurrenceDayOfMonth + ' is an invalid daysOfMonthMask';
    }

    let resultDaysOfMothArr: number[] = [];
    // special case of Last
    if (daysOfMonthMask === 0) {
      resultDaysOfMothArr.push(0);
    }

    let dayNum = 1;
    while (daysOfMonthMask > 0) {
      if (daysOfMonthMask & 1) {
        resultDaysOfMothArr.push(dayNum);
      }
      dayNum++;
      daysOfMonthMask = daysOfMonthMask >>> 1;
    }

    return resultDaysOfMothArr;
  }

  /**
   * Warning: May throw unexpected exceptions if conversion fails
   */
  export function fromSSSchedule(schedule: ISchedule): ISchedule {
    // This function modifies key attributes and as such needs to make a copy of the data to avoid any unexpected side-effects
    const scheduleClone: ISchedule = clone(schedule);
    switch (scheduleClone.scheduleType) { // Hourly, Daily, Weekly, Monthly
      case 'Hourly':
        if ((scheduleClone.scheduleDetails as HourlyRecurrenceDetails).recurrenceDaysOfWeek && (scheduleClone.scheduleDetails as HourlyRecurrenceDetails).recurrenceDaysOfWeek.length === 7) {
          delete (scheduleClone.scheduleDetails as HourlyRecurrenceDetails).recurrenceDaysOfWeek;
        }
        break;
      case 'Daily':
        if ((scheduleClone.scheduleDetails as DailyRecurrenceDetails).recurrenceDaysOfWeek && (scheduleClone.scheduleDetails as HourlyRecurrenceDetails).recurrenceDaysOfWeek.length === 7) {
          delete (scheduleClone.scheduleDetails as DailyRecurrenceDetails).recurrenceDaysOfWeek;
        }
        // This is actually due to an issue with old schedules
        if ((scheduleClone.scheduleDetails as DailyRecurrenceDetails).recurrenceMinutes < 120 || !((scheduleClone.scheduleDetails as DailyRecurrenceDetails).recurrenceMinutes)) {
          (scheduleClone.scheduleDetails as DailyRecurrenceDetails).recurrenceMinutes = 24 * 60;
        }

        // Schedules created by scripts can have nonsensical end times - endTime, in theory, should not matter for Daily schedules
        const twentyFourHourRecurrence: boolean = (scheduleClone.scheduleDetails as DailyRecurrenceDetails).recurrenceMinutes === 24 * 60;
        const noEndTime = (scheduleClone.scheduleDetails as DailyRecurrenceDetails).endTime === undefined;
        const invalidEndTime = (scheduleClone.scheduleDetails as DailyRecurrenceDetails).endTime !== (scheduleClone.scheduleDetails as DailyRecurrenceDetails).startTime;
        if (twentyFourHourRecurrence && (noEndTime || invalidEndTime)) {
          (scheduleClone.scheduleDetails as DailyRecurrenceDetails).endTime = (scheduleClone.scheduleDetails as DailyRecurrenceDetails).startTime;
        }
        break;
      case 'Monthly':
        if ((scheduleClone.scheduleDetails as MonthlyRecurrenceDetails).recurrenceDaysOfWeek !== undefined && (scheduleClone.scheduleDetails as MonthlyRecurrenceDetails).recurrenceDaysOfWeek.length !== 0) {
          throw 'Failed to convert schedule: cannot convert Monthly schedule with days of week set';
        }
        let daysOfMonthMask = (scheduleClone.scheduleDetails as MonthlyRecurrenceDetails).recurrenceDayOfMonth;
        let daysOfMonth = toDaysOfMonthArr(daysOfMonthMask);
        if (daysOfMonth.length !== 1) {
          throw `Failed to convert schedule: multiple days of month present (${daysOfMonthMask} => ${JSON.stringify(daysOfMonth)})`;
        }
        // Interestingly the legacy monthly logic actually expects these schedules to use SSS formatted daysOfMonth.
        break;
    }
    return scheduleClone;
  }

  export function areNewDescriptionsEnabled(schedule: ISchedule, isLegacyOverride?: boolean): boolean {
    if (isLegacyOverride !== undefined && isLegacyOverride) {
      return false; // if isLegacy is true then new descriptions are disabled (its an override param)
    }
    const isLegacySchedule = schedule.definedBy !== DefinedBy.SELF_SERVICE && !FeatureFlags.adminSelfServiceSchedulesEnabled;
    if (isLegacySchedule) {
      return false; // if it's a legacy schedule the new description logic will not work as expected
    }
    const isOnline = SessionServerSelectors.isOnlineEnvironment(getAppStore().getState());
    if (!isOnline) {
      return true; // In non-Online enviornments we now use the new descriptions for all non legacy scheduls
    }
    if (schedule.scheduledAction === ScheduleActions.Flow) {
      return isFlowSelfServiceSchedulesEnabled();
    }
    const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
    const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
    return isExtractSSSEnabledForServer && isExtractSSSEnabledForSite; // In online the FF we are using to deploy new descriptions is the Refresh SSS one
  }

  export function getScheduleDescription(schedule: ISchedule, clientTimezoneOffsetOverride?: number, isLegacyOverride?: boolean): string {
    if (!areNewDescriptionsEnabled(schedule, isLegacyOverride)) {
      return describeLegacyAdminSchedule(schedule);
    }

    const sssSchedule = RecurrenceConverters.castScheduleToRecurrence(schedule);
    if (schedule.timeZoneDisplayName) {
      // In this case we localize it in created time (no TZ conversion) and append the TZ
      let description = ScheduleDescriptions.localizeRecurrence(sssSchedule);
      return `${description} ${schedule.timeZoneDisplayName}`;
    }

    const conversionParams: ScheduleDescriptions.TimeZoneConversionParams = {
      serverTimeUtcOffsetMinutes: schedule.serverTimezoneOffset,
      localTimeUtcOffsetMinutes: clientTimezoneOffsetOverride !== undefined
        ? clientTimezoneOffsetOverride
        : -(new Date()).getTimezoneOffset(), // JS returns -180 for UTC +3
    };
    return ScheduleDescriptions.localizeRecurrenceWithOffset(sssSchedule, conversionParams);
  }

  export function getRecurrenceDescription(recurrence: Recurrence): string {
    const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
    const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
    if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
      return ScheduleDescriptions.localizeRecurrenceWithOffset(recurrence);
    }

    try {
      const legacySchedule = fromSSSchedule({
        serverTimezoneOffset: 0,
        scheduleType: recurrence.recurrenceType,
        scheduleDetails: recurrence.recurrenceDetails,
      } as unknown as ISchedule);
      const legacyDescription = describeLegacyAdminSchedule(legacySchedule);
      return legacyDescription;
    } catch (exception) {
      // No need for converting anything in this case
      return LocalizationHelpers.getRecurrenceStringLocalized(recurrence);
    }
  }
}
